import { memo, useContext } from 'react';
import { ChatRendererProps } from './types';
import StepWithMaybeResponse from '../StepWithMaybeResponse';
import { CompletedTurn } from '../CompletedTurn';
import {
  Attachment,
  InteractionStepType,
  ReferenceText,
  MessageRole,
  MultiTurnContinueStepResponse,
} from '@scale/shared/types/chat';
import {
  useChatStoreExtract,
  useIsTurnCompleted,
  useTurnByIndex,
  useNumberOfTurns,
  useLastResponseGeneratorStep,
  useIsAnyTurnIncomplete,
  useMultiTurnContinueResponseForTurnIndex,
  useChatStoreRef,
} from '../../Chat/hooks/ChatStore.hooks';
import { ChatTaskContext } from '../contexts/ChatTaskContext';
import { usePerStepResponseLinter } from '../hooks/usePerStepResponseLinter/hook';
import { usePerStepContributorFeedback } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/hooks/usePerStepContributorFeedback';
import { useLocalStorageBackupStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useLocalStorageBackupStore';
import { useContributorFeedbackImperativeApi } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';

type TurnRendererProps = Pick<ChatRendererProps, 'readOnly' | 'collapsibleInstruction'> & {
  turnIndex: number;
  preventScrollToBottom?: boolean;
  inlineMode?: boolean;
};
const TurnRendererComponent = ({
  readOnly,
  turnIndex,
  preventScrollToBottom,
  collapsibleInstruction,
  inlineMode = true,
}: TurnRendererProps) => {
  const taskContext = useContext(ChatTaskContext);
  const chatStoreRef = useChatStoreRef();
  const contributorFeedbackStoreInstance = useContributorFeedbackImperativeApi();

  const config = useChatStoreExtract(state => state.config);
  const setResponseForIndex = useChatStoreExtract(state => state.setResponseForIndex);
  const turnSteps = useTurnByIndex(turnIndex);
  const isTurnCompleted = useIsTurnCompleted(turnIndex);
  const numberOfTurns = useNumberOfTurns();
  const lastResponseGeneratorStep = useLastResponseGeneratorStep();
  const isAnyTurnIncomplete = useIsAnyTurnIncomplete();
  const promptResponse = useMultiTurnContinueResponseForTurnIndex(turnIndex);
  const bulkClearBackupsForAssignment = useLocalStorageBackupStore(
    state => state.bulkClearBackupsForAssignment,
  );
  const resetWorkerCommentsFromResponseIndex = useWorkerCommentsStore(
    state => state.resetWorkerCommentsFromResponseIndex,
  );
  const { lintStepResponse } = usePerStepResponseLinter();

  if (!turnSteps || turnSteps.length === 0) return null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const contributorFeedbackHook = usePerStepContributorFeedback({
    stepParams: turnSteps[turnSteps.length - 1],
    // TODO: Generally, we the cases where we want to disable linters (i.e preview pages, audits, etc), we also want to disable other
    // automated feedback. But there could be future cases where this isn't true.
    disableFeedback: taskContext.disableLinter,
  });

  const enableRTLText =
    lastResponseGeneratorStep && 'enable_rtl_text' in lastResponseGeneratorStep.params
      ? lastResponseGeneratorStep.params.enable_rtl_text
      : false;

  if (isTurnCompleted && inlineMode) {
    const childId = `${InteractionStepType.MultiTurnContinue}-${turnSteps.at(-1)?.responseIndex}`;
    return (
      <CompletedTurn
        key={turnIndex}
        turnIndex={turnIndex}
        totalTurns={numberOfTurns}
        isAnyTurnIncomplete={isAnyTurnIncomplete}
        readOnly={readOnly}
        preventScrollToBottom={preventScrollToBottom}
        onUpdatePrompt={async (
          newPrompt: string,
          role: MessageRole,
          shouldResetHistory: boolean,
          editedReferenceTexts: ReferenceText[],
          editedAttachments: Attachment[],
          skipPromptInput: boolean,
        ) => {
          if (!promptResponse) return false;
          const newResponse: MultiTurnContinueStepResponse = {
            type: InteractionStepType.MultiTurnContinue,
            context: {
              ...promptResponse.context,
              referenceTexts: editedReferenceTexts,
              attachments: editedAttachments,
              role,
              skipPromptInput,
            },
            output: newPrompt,
          };

          const currentState = chatStoreRef.current.getState();

          // TODO: Extract into a separate function since this is used in a few places (TurnRenderer.tsx, useStepSubmitHandler.ts)
          let isValid: boolean | undefined = true;
          if (taskContext.enableUnifiedFeedback) {
            isValid = await contributorFeedbackHook.fetchContributorFeedbackOnStepSubmit(
              newResponse,
              promptResponse.index,
            );
          } else {
            isValid = await lintStepResponse(
              turnSteps[turnSteps.length - 1],
              newResponse,
              promptResponse.index,
            );
          }

          if (!isValid) {
            return false;
          }

          if (shouldResetHistory && taskContext.assignmentId) {
            bulkClearBackupsForAssignment(taskContext.assignmentId, promptResponse.index);
          }

          const allStepParams = currentState.stepParams;
          if (shouldResetHistory && allStepParams) {
            if (taskContext.enableUnifiedFeedback) {
              contributorFeedbackStoreInstance
                .getState()
                .resetAutomatedContributorFeedbackFromResponseIndex(
                  allStepParams,
                  promptResponse.index,
                );
            }
            resetWorkerCommentsFromResponseIndex(allStepParams, promptResponse.index);
          }

          setResponseForIndex(newResponse, promptResponse.index, shouldResetHistory);
          return true;
        }}
        workerCommentProps={{
          showCommentIcon: true,
          childId,
          canEdit: !!taskContext.canEditComments,
          areCommentsReadOnly: !!taskContext.areCommentsReadOnly,
          turnIndex: turnIndex + 1,
        }}
        responseTemplatingProps={{
          showResponseTemplatingIcon: !!taskContext.responseTemplatingMode,
          childId,
          metadata: {
            stepIndex: turnSteps.at(-1)?.responseIndex,
          },
        }}
        enableRTLText={enableRTLText}
      />
    );
  }
  return (
    <>
      {turnSteps.map(step => {
        if (!inlineMode && step.type === InteractionStepType.MultiTurnContinue) return null;
        return (
          <StepWithMaybeResponse
            key={`turn-${turnIndex}-step-${step.responseIndex}`}
            responseIndex={step.responseIndex}
            readOnly={readOnly}
            preventScrollToBottom={preventScrollToBottom}
            collapsibleInstruction={collapsibleInstruction}
            extractModelResponseRegex={config.extractModelResponseRegex}
          />
        );
      })}
    </>
  );
};

export const TurnRenderer = memo(TurnRendererComponent);
